"use client"

import { useState } from "react"
import type { User } from "@/lib/auth-types"
import { Button } from "@/components/ui/button"
import { AlertTriangle, X } from "lucide-react"

interface MasqueradeBannerProps {
  user: User
}

export function MasqueradeBanner({ user }: MasqueradeBannerProps) {
  const [isEnding, setIsEnding] = useState(false)

  const handleEndMasquerade = async () => {
    setIsEnding(true)
    try {
      const response = await fetch("/api/admin/masquerade", {
        method: "DELETE",
      })
      
      if (response.ok) {
        // Force a hard refresh to clear any cached user data
        window.location.href = window.location.pathname
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error("Failed to end masquerade:", errorData)
        alert("Failed to end masquerade session. Please try again.")
      }
    } catch (error) {
      console.error("Error ending masquerade:", error)
      alert("Error ending masquerade session. Please try again.")
    } finally {
      setIsEnding(false)
    }
  }

  return (
    <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <div className="text-sm text-yellow-800">
            <span className="font-medium">Masquerading as:</span>{" "}
            <span className="font-semibold">{user.name}</span>
            {user.masqueradedBy && (
              <span className="ml-2 text-yellow-700">
                (Admin: {user.masqueradedBy.name})
              </span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleEndMasquerade}
          disabled={isEnding}
          className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
        >
          {isEnding ? "Ending..." : "End Masquerade"}
        </Button>
      </div>
    </div>
  )
} 